import { forwardRef, useId, type InputHTMLAttributes } from "react";
import { HintMark } from "./Tooltip";

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  /** Short explanation shown via the label info mark. */
  tooltip?: string;
};

/** Base text input. Rounded, soft border, no visual weight until focused. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, tooltip, style, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? (label ? generatedId : undefined);
  const input = (
    <input
      ref={ref}
      id={inputId}
      {...rest}
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--color-border)",
        background: "var(--color-input)",
        color: "var(--color-text)",
        fontSize: "var(--font-size-md)",
        fontFamily: "var(--font-sans)",
        minHeight: "var(--touch-target)",
        transition: "border-color var(--duration-fast) var(--ease-standard)",
        ...style,
      }}
    />
  );
  if (label) {
    return (
      <div style={{ display: "grid", gap: "var(--space-1)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
          <label htmlFor={inputId}>{label}</label>
          {tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}
        </span>
        {input}
      </div>
    );
  }
  if (tooltip) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
        {input}
        <HintMark label="More about this" text={tooltip} />
      </span>
    );
  }
  return input;
});
